import { useCallback, useEffect, useRef } from 'react';
import { useAPI } from 'utils/api';
import { useGlobal } from 'context/global/GlobalContext';
import { CookieSettings } from 'types/ProductDetailsType';
import { EffectiveConsent, getEffectiveConsent } from 'utils/getCookieConsent';
import {
  PrivacyOptOutSource,
  getPrivacyOptOut,
  isPrivacyOptOutSynced,
  markPrivacyOptOutSynced,
  clearPrivacyOptOutSynced,
  isAdminPreviewOrEmbedded,
} from 'utils/privacyOptOut';

type PrivacyOptOutPayload = {
  brandId: string;
  source: PrivacyOptOutSource;
  action?: 'opt-out' | 'lift';
  effectiveConsent?: EffectiveConsent;
};

type PrivacyOptOutResponse = {
  success?: boolean;
  skipped?: string;
  doNotSell?: Record<string, unknown> | null;
};

type UsePrivacyOptOutSyncArgs = {
  // The brand the opt-out is recorded for; nothing is written without one.
  brandId?: string | null;
  // The brand's banner settings, for the default sent as effectiveConsent
  // when the visitor never decided (getEffectiveConsent). Only used when the
  // settings are known to belong to that brand.
  cookieSettings?: CookieSettings | null;
  settingsKnown?: boolean;
  // Record a standing opt-out (Do Not Sell marker or GPC) on its own, once
  // per session per brand, as soon as the signed-in user's profile is loaded.
  autoSync?: boolean;
};

// SIG-114: records a Do Not Sell or GPC opt-out on the signed-in user's
// profile through POST auth/privacy-opt-out. Never from admin registration,
// preview or embedded frames, never blocks rendering, and failures are only
// logged. A brand is marked synced for the session only once the write lands.
const usePrivacyOptOutSync = ({
  brandId,
  cookieSettings = null,
  settingsKnown = false,
  autoSync = false,
}: UsePrivacyOptOutSyncArgs) => {
  const { user, personalDetails } = useGlobal();
  const [postPrivacyOptOut] = useAPI<PrivacyOptOutPayload>({
    method: 'POST',
    endpoint: 'auth/privacy-opt-out',
  });

  // Latest values for the async writes, read when they run so they add no
  // effect dependencies.
  const latest = useRef({
    postPrivacyOptOut,
    user,
    cookieSettings,
    settingsKnown,
  });
  latest.current = { postPrivacyOptOut, user, cookieSettings, settingsKnown };
  const inFlight = useRef<Set<string>>(new Set());

  const syncOptOut = useCallback(
    async (source?: PrivacyOptOutSource): Promise<boolean> => {
      try {
        const current = latest.current;
        if (!brandId || !current.user || isAdminPreviewOrEmbedded()) {
          return false;
        }
        // GPC ranks ahead of the marker, as in the send gates, so a browser
        // with both still records the GPC opt-out (meta.gpc) on the profile.
        const optOut = getPrivacyOptOut();
        const optOutSource: PrivacyOptOutSource | null =
          source ||
          (optOut.gpc === true
            ? 'gpc'
            : optOut.doNotSell === true
            ? 'dns_link'
            : null);
        if (!optOutSource || inFlight.current.has(brandId)) return false;

        const payload: PrivacyOptOutPayload = {
          brandId,
          source: optOutSource,
        };
        const effectiveConsent = getEffectiveConsent(
          current.cookieSettings,
          current.settingsKnown
        );
        if (effectiveConsent) payload.effectiveConsent = effectiveConsent;

        inFlight.current.add(brandId);
        try {
          const response: PrivacyOptOutResponse | undefined =
            await current.postPrivacyOptOut(payload);
          if (response?.success === true && !response?.skipped) {
            markPrivacyOptOutSynced(brandId, current.user.uid);
            return true;
          }
          console.warn(
            'PRIVACY OPT-OUT SYNC NOT RECORDED: ',
            response?.skipped || response
          );
          return false;
        } finally {
          inFlight.current.delete(brandId);
        }
      } catch (error) {
        console.warn('PRIVACY OPT-OUT SYNC FAILED: ', error);
        return false;
      }
    },
    [brandId]
  );

  // Opt back in: lifts the standing opt-out on the profile. The session flag
  // is cleared either way, so a later opt-out is written again. True once the
  // profile holds no standing opt-out for the brand.
  const liftOptOut = useCallback(async (): Promise<boolean> => {
    try {
      const current = latest.current;
      if (!brandId) return false;
      if (current.user?.uid)
        clearPrivacyOptOutSynced(brandId, current.user.uid);
      if (!current.user || isAdminPreviewOrEmbedded()) return false;
      const response: PrivacyOptOutResponse | undefined =
        await current.postPrivacyOptOut({
          brandId,
          source: 'dns_link',
          action: 'lift',
        });
      // A success that still reports a standing opt-out is not a lift.
      return response?.success === true && response?.doNotSell?.value !== true;
    } catch (error) {
      console.warn('PRIVACY OPT-OUT LIFT FAILED: ', error);
      return false;
    }
  }, [brandId]);

  const optOutActive = getPrivacyOptOut().active;
  const userId = user?.uid;
  const profileLoaded = !!personalDetails;

  useEffect(() => {
    if (!autoSync || !optOutActive || !brandId || !userId || !profileLoaded) {
      return;
    }
    if (isAdminPreviewOrEmbedded() || isPrivacyOptOutSynced(brandId, userId)) {
      return;
    }
    void syncOptOut();
  }, [autoSync, optOutActive, brandId, userId, profileLoaded, syncOptOut]);

  return { syncOptOut, liftOptOut };
};

export default usePrivacyOptOutSync;
